import React, { useState, useEffect } from 'react';
import { FiX, FiDownload, FiShare2, FiBookmark, FiHeart, FiMessageCircle, FiExternalLink, FiAward, FiFlag, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { API_URL, CATEGORIES, REACTIONS } from '../../utils/constants';
import { commentsAPI, favoritesAPI, postsAPI, reactionsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import CommentSection from '../CommentSection/CommentSection';
import toast from 'react-hot-toast';
import { resolveMediaUrl } from '../../utils/media';
import { MdVerified } from 'react-icons/md';
import './ArticleModal.css';
import ReportModal from '../ReportModal/ReportModal';

const ArticleModal = ({ article, onClose }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [reaction, setReaction] = useState(null);
  const [comments, setComments] = useState([]);
  const [reactionCounts, setReactionCounts] = useState({});
  const [articleData, setArticleData] = useState(article);
  const [reportOpen, setReportOpen] = useState(false);

  const slugify = (text) => {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const getArticleSlug = () => {
    if (articleData?.slug) return articleData.slug;
    const base = slugify(articleData?.title) || 'post';
    return articleData?.id ? `${base}-${articleData.id}` : base;
  };

  useEffect(() => {
    loadFullArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article.id]);

  const loadFullArticle = async () => {
    try {
      const [postRes, commentsRes] = await Promise.all([
        postsAPI.getById(article.id),
        commentsAPI.getByPostId(article.id)
      ]);
      setArticleData(postRes.data);
      setComments(commentsRes.data || []);

      const [reactionsRes, userReactionRes, favRes] = await Promise.all([
        reactionsAPI.getByPostId(article.id).catch(() => ({ data: {} })),
        isAuthenticated ? reactionsAPI.getUserReaction(article.id).catch(() => ({ data: { userReaction: null } })) : Promise.resolve({ data: { userReaction: null } }),
        isAuthenticated ? favoritesAPI.check(article.id).catch(() => ({ data: {} })) : Promise.resolve({ data: {} })
      ]);

      setReactionCounts(reactionsRes.data?.reactions || reactionsRes.data?.counts || reactionsRes.data || {});
      setReaction(userReactionRes.data?.userReaction || null);
      setIsSaved(Boolean(favRes?.data?.isFavorite));
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const category = CATEGORIES.find(cat => cat.id === articleData.category);

  const handleDownloadPDF = () => {
    const url = `${API_URL}/posts/${articleData.id}/pdf`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleShare = () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para compartir');
      return;
    }
        const url = `${window.location.origin}/article/${getArticleSlug()}`;
    if (navigator.share) {
      navigator.share({
        title: articleData.title,
        text: articleData.abstract,
        url
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('¡Enlace copiado!');
    }
  };

  const handleReaction = async (reactionId) => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para reaccionar');
      return;
    }
    try {
      const res = await reactionsAPI.toggle(articleData.id, reactionId);
      setReaction(res.data?.userReaction || null);
      setReactionCounts(res.data?.reactions || {});
    } catch (error) {
      console.error('Error toggling reaction:', error);
      toast.error('No se pudo reaccionar');
    }
  };

  const handleSave = () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para guardar');
      return;
    }
    (async () => {
      try {
        if (isSaved) {
          await favoritesAPI.remove(articleData.id);
          setIsSaved(false);
          toast.success('Eliminado de guardados');
        } else {
          await favoritesAPI.add(articleData.id);
          setIsSaved(true);
          toast.success('¡Guardado!');
        }
      } catch (error) {
        console.error('Error toggling favorite:', error);
        toast.error('Error al actualizar guardados');
      }
    })();
  };

  const isOwner = Boolean(user?.id && articleData?.author?.id && user.id === articleData.author.id);

  const handleEdit = () => {
    navigate(`/posts/edit/${articleData.id}`, { replace: true, state: {} });
  };

  const handleDeletePost = async () => {
    if (!window.confirm('¿Eliminar esta publicación? Esta acción no se puede deshacer.')) return;
    try {
      await postsAPI.deletePost(articleData.id);
      toast.success('Publicación eliminada');
      navigate('/');
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('No se pudo eliminar');
    }
  };

  // Mapeo de section_type a labels en español
  const sectionLabels = {
    'abstract': 'Resumen',
    'intro': 'Introducción',
    'section': 'Sección',
    'subsection': 'Subsección',
    'conclusion': 'Conclusiones',
    'bibliography': 'Bibliografía'
  };

  // Determinar qué secciones usar - PRIORIDAD: article_sections con figuras
  const getSections = () => {
    // 1. PRIORIDAD: Secciones de article_sections (tienen figuras)
    if (articleData.sections && articleData.sections.length > 0) {
      return articleData.sections.map(section => ({
        id: section.id || `section-${section.position}`,
        section_type: section.section_type,
        title: section.title || sectionLabels[section.section_type] || section.section_type,
        content: section.content,
        images: section.images?.filter(img => img.id) || []
      }));
    }
    // 2. Campos directos de la DB (abstract, introduction, etc.)
    if (articleData.abstract || articleData.introduction) {
      return [
        { id: 'abstract', title: 'Resumen', content: articleData.abstract, images: [] },
        { id: 'introduction', title: 'Introducción', content: articleData.introduction, images: [] },
        { id: 'framework', title: 'Marco Teórico', content: articleData.theoretical_framework, images: [] },
        { id: 'methodology', title: 'Metodología', content: articleData.methodology, images: [] },
        { id: 'results', title: 'Resultados', content: articleData.results, images: [] },
        { id: 'discussion', title: 'Discusión', content: articleData.discussion, images: [] },
        { id: 'conclusions', title: 'Conclusiones', content: articleData.conclusions, images: [] }
      ].filter(s => s.content);
    }
    return [];
  };

  const sections = getSections();

  // Render a section with its images
  const renderSection = (section) => {
    return (
      <section key={section.id} className={`article-section section-${section.section_type || section.id}`}>
        <h2 className="section-title">{section.title}</h2>
        <p className="section-text">{section.content}</p>
        
        {/* Section images */}
        {section.images && section.images.length > 0 && (
          <div className="section-figures">
            {section.images.map((img, idx) => (
              <figure key={idx} className="article-figure">
                <img src={resolveMediaUrl(img.image_url)} alt={img.alt_text || section.title} />
                {img.caption && <figcaption>{img.caption}</figcaption>}
              </figure>
            ))}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="article-modal-overlay" onClick={onClose}>
      <div className="article-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <header className="modal-header">
          <Link 
            to={`/article/${getArticleSlug()}`} 
            className="open-page-btn"
            title="Abrir en página completa"
          >
            <FiExternalLink />
          </Link>
          <button className="close-btn" onClick={onClose}>
            <FiX />
          </button>
        </header>

        {/* Cover Image */}
        {articleData.image && (
          <div className="modal-cover">
            <img src={resolveMediaUrl(articleData.image)} alt={articleData.title} />
            {category && (
              <div className="modal-category" style={{ background: category.color }}>
                {category.icon} {category.name}
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="modal-content">
          {/* Journal-style Header */}
          <div className="journal-header">
            <span className="journal-name">GRAPHYRA</span>
            <span className="journal-issue">Revista de Conocimiento Científico</span>
          </div>

          {/* Title and Metadata */}
          <div className="article-header">
            <h1>{articleData.title}</h1>
            
            {/* Autor - Usuario que publicó */}
            <div className="article-author-info">
              <Link
                to={`/${articleData.author?.username || articleData.author_id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'inherit' }}
              >
                <img 
                  src={resolveMediaUrl(articleData.author?.avatar) || `https://i.pravatar.cc/50?u=${articleData.author_id}`}
                  alt={articleData.author?.name}
                  className="author-avatar"
                />
                <div className="author-details">
                  <strong>
                    {articleData.author?.name || 'Usuario'}
                    {articleData.author?.verification_level && articleData.author.verification_level !== 'none' && (
                      <MdVerified
                        style={{
                          marginLeft: 6,
                          verticalAlign: 'middle',
                          color: articleData.author.badge_color || 'currentColor'
                        }}
                        title={articleData.author.badge_label || 'Cuenta verificada'}
                      />
                    )}
                  </strong>
                  <span>{new Date(articleData.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
              </Link>
            </div>
            
            <div className="article-authors">
              {articleData.authors?.map((author, index) => (
                <div key={index} className="author-item">
                  <span className="author-name">{author.name}</span>
                  {author.affiliation && (
                    <span className="author-affiliation">{author.affiliation}</span>
                  )}
                </div>
              ))}
            </div>

            {articleData.keywords && articleData.keywords.length > 0 && (
              <div className="article-keywords">
                <strong>Palabras clave:</strong>
                {articleData.keywords.filter(k => k).map((keyword, index) => (
                  <span key={index} className="keyword-tag">{keyword}</span>
                ))}
              </div>
            )}
          </div>

          {/* Sections - usando la lógica unificada */}
          {sections.length > 0 ? (
            <div className="article-sections">
              {sections.map(section => renderSection(section))}
            </div>
          ) : articleData.content ? (
            <div className="article-section">
              <div dangerouslySetInnerHTML={{ __html: articleData.content }} />
            </div>
          ) : null}

          {/* Bibliography */}
          {articleData.bibliography && articleData.bibliography.filter(r => r).length > 0 && (
            <section className="article-section">
              <h2>Referencias</h2>
              <ol className="references-list">
                {articleData.bibliography.filter(r => r).map((ref, index) => (
                  <li key={index}>{ref}</li>
                ))}
              </ol>
            </section>
          )}

          {/* Footer with attribution */}
          <div className="article-footer">
            <div className="graphyra-attribution">
              <FiAward />
              <span>Publicado en GRAPHYRA - Plataforma de Conocimiento Científico</span>
            </div>
          </div>
        </div>

        {/* Sticky Action Bar */}
        <div className="modal-actions">
          {/* Reactions - Solo para usuarios logueados */}
          {isAuthenticated ? (
            <div className="reactions-bar">
              <span className="reactions-label">Reacciones:</span>
              <div className="reactions-list">
                {REACTIONS.map(r => (
                  <button
                    key={r.id}
                    className={`reaction-btn ${reaction === r.id ? 'active' : ''}`}
                    onClick={() => handleReaction(r.id)}
                    title={r.label}
                  >
                    <span className="reaction-emoji">{r.emoji}</span>
                    <span className="reaction-count">{reactionCounts[r.id] || 0}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="login-prompt-bar">
              <span>💡 <Link to="/login">Inicia sesión</Link> para reaccionar</span>
            </div>
          )}

          {/* Main Actions */}
          <div className="main-actions">
            <button
              className={`action-btn ${reaction === 'love' ? 'active' : ''}`}
              onClick={() => handleReaction('love')}
            >
              <FiHeart />
              <span>{reactionCounts?.love || 0}</span>
            </button>

            <button
              className="action-btn"
              onClick={() => setShowComments(!showComments)}
            >
              <FiMessageCircle />
              <span>{comments.length}</span>
            </button>

            <button className="action-btn" onClick={handleDownloadPDF}>
              <FiDownload />
              <span>PDF</span>
            </button>

            <button className="action-btn" onClick={handleShare}>
              <FiShare2 />
              <span>Compartir</span>
            </button>

            <button
              className={`action-btn ${isSaved ? 'active' : ''}`}
              onClick={handleSave}
            >
              <FiBookmark />
            </button>

            <button
              className="action-btn"
              onClick={() => {
                if (!isAuthenticated) {
                  toast.error('Inicia sesión para reportar');
                  return;
                }
                setReportOpen(true);
              }}
            >
              <FiFlag />
            </button>

            {isOwner && (
              <>
                <button className="action-btn" onClick={handleEdit} title="Editar">
                  <FiEdit2 />
                </button>
                <button className="action-btn" onClick={handleDeletePost} title="Eliminar">
                  <FiTrash2 />
                </button>
              </>
            )}
          </div>
        </div>

        <ReportModal
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          onSubmit={async ({ reason, description }) => {
            try {
              await postsAPI.report(articleData.id, { reason, description });
              toast.success('Reporte enviado');
              setReportOpen(false);
            } catch (error) {
              console.error('Error submitting report:', error);
              toast.error('No se pudo enviar el reporte');
            }
          }}
        />

        {/* Comments Section */}
        {showComments && (
          <div className="comments-section">
            <CommentSection
              postId={articleData.id}
              comments={comments}
              onAddComment={async (data) => {
                if (!isAuthenticated) {
                  toast.error('Inicia sesión para comentar');
                  return;
                }
                try {
                  await commentsAPI.create({
                    postId: articleData.id,
                    content: data.text,
                    parentId: data.parentId
                  });
                  loadFullArticle();
                  toast.success('Comentario añadido');
                } catch (error) {
                  toast.error('Error al añadir comentario');
                }
              }}
              onEditComment={async (id, data) => {
                try {
                  await commentsAPI.update(id, data);
                  loadFullArticle();
                } catch (error) {
                  toast.error('Error al editar');
                }
              }}
              onDeleteComment={async (id) => {
                try {
                  await commentsAPI.delete(id);
                  loadFullArticle();
                } catch (error) {
                  toast.error('Error al eliminar');
                }
              }}
              onRefresh={loadFullArticle}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleModal;
